import { GamepadActionId } from 'generated/graphql';
import { identifyDictaphoneProducts } from 'utils/dictaphone/products';
import type { DictaphoneButton, DictaphoneProduct } from 'utils/dictaphone/products/types';
import { VIRTUAL_DICTAPHONE_AUDIO_INPUT_DEVICE_ID } from 'utils/dictaphone/products/virtual';

export type DictaphoneActionInputMode = 'gamepad' | 'webhid';

export type DictaphoneActionMetricPayload = {
  actionId: GamepadActionId;
  audioInputDeviceLabel: string;
  dictaphoneProductId: string;
  inputMode: DictaphoneActionInputMode;
  keyCode: string;
  keyName: string | null;
  isRecording: boolean;
};

type BuildDictaphoneActionMetricParams = {
  action: GamepadActionId;
  audioInputDeviceLabel: string;
  dictaphoneProductId?: string;
  dictaphoneButtonNameByKeyCode: ReadonlyMap<string, string>;
  isRecording: boolean;
} & (
  | {
      inputMode: 'gamepad';
      gamepadKeyCode?: string;
    }
  | {
      inputMode: 'webhid';
      webhidValue: number;
    }
);

export const getMatchedDictaphoneProduct = ({
  deviceLabel,
  audioInputDeviceId,
}: {
  deviceLabel: string;
  audioInputDeviceId: string;
}): DictaphoneProduct | undefined => {
  const matchingProduct = identifyDictaphoneProducts({ deviceLabel }).values().next().value;
  if (matchingProduct == null) {
    return undefined;
  }

  // Keep local virtual gamepad behavior broad in dev/test, but only attribute
  // virtual product metrics when the virtual audio input is explicitly selected.
  if (
    matchingProduct.id === VIRTUAL_DICTAPHONE_AUDIO_INPUT_DEVICE_ID &&
    audioInputDeviceId !== VIRTUAL_DICTAPHONE_AUDIO_INPUT_DEVICE_ID
  ) {
    return undefined;
  }

  return matchingProduct;
};

export const createDictaphoneButtonNameByKeyCode = (
  buttons: ReadonlyArray<DictaphoneButton>
): ReadonlyMap<string, string> =>
  new Map(buttons.map(({ keyCode, name }) => [String(keyCode), name]));

export const buildDictaphoneActionMetric = (
  params: BuildDictaphoneActionMetricParams
): DictaphoneActionMetricPayload => {
  const {
    action,
    audioInputDeviceLabel,
    dictaphoneProductId,
    dictaphoneButtonNameByKeyCode,
    isRecording,
    inputMode,
  } = params;
  const normalizedKeyCode =
    inputMode === 'webhid' ? String(params.webhidValue) : (params.gamepadKeyCode ?? 'unknown');
  const keyName =
    inputMode === 'webhid' ? null : (dictaphoneButtonNameByKeyCode.get(normalizedKeyCode) ?? null);

  return {
    actionId: action,
    audioInputDeviceLabel,
    dictaphoneProductId: dictaphoneProductId ?? 'unknown',
    inputMode,
    keyCode: normalizedKeyCode,
    keyName,
    isRecording,
  };
};
